import classNames from "classnames";
import React, { FunctionComponent, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import {
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../../../utils/customization/finders";
import { RotationPagesMappingCollection } from "../../../../utils/customization/page";
import { countEditablePicturesInPage } from "../../../../utils/customization/template";
import MockPage from "../MockPage";
import "./style.scss";

const MAX_LAYOUT_SIZE = 75;

interface Props {
  currentPageDefinitionKey?: string;
  pageKey?: string;
  rotatedPairs?: RotationPagesMappingCollection;
  undoKey?: string;
}

const PageLayouts: FunctionComponent<Props> = ({
  currentPageDefinitionKey,
  pageKey,
  rotatedPairs,
  undoKey,
}) => {
  const { configuration, content, dispatch, template } = useCustomizationContext();
  const pageLayoutsElement = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  const filteredPageDefinitions = useMemo(() => {
    const filtered = findPageDefinitionsWithValidCondition(
      template,
      content,
      {},
      configuration?.options
    );
    return filtered && getNoCoverDefinitions(filtered);
  }, [configuration?.options, content, template]);

  const groupedPageDefinitions = filteredPageDefinitions.reduce(
    (
      grouped: { [key: string]: { title: string; templates: TemplatePageDefinition[] } },
      pageDefinition
    ) => {
      const photosCount = countEditablePicturesInPage(pageDefinition);
      const photosCountTitle =
        photosCount > 0
          ? intl.formatMessage(
              { id: "kustom_PageLayouts_group_title_count" },
              { count: photosCount }
            )
          : intl.formatMessage({ id: "kustom_PageLayouts_group_title_text" });
      const photosCountKey = photosCount > 0 ? photosCount : 1000; // 1000 to have layouts without images last
      if (grouped[photosCountKey]) {
        grouped[photosCountKey].templates.push(pageDefinition);
      } else {
        grouped[photosCountKey] = { title: photosCountTitle, templates: [pageDefinition] };
      }
      return grouped;
    },
    {}
  );

  const layoutsGroupKeys = Object.keys(groupedPageDefinitions);
  layoutsGroupKeys.sort((a, b) => +a - +b);

  let selectedPageKey = "";
  if (currentPageDefinitionKey) {
    selectedPageKey = currentPageDefinitionKey;
  } else {
    const pageDefinitionKeys = [
      ...new Set(content.pages.map(({ pageDefinitionKey }) => pageDefinitionKey)),
    ];
    if (pageDefinitionKeys.length === 1) {
      selectedPageKey = pageDefinitionKeys[0];
    }
  }

  let onClick: (pageDefinition: TemplatePageDefinition) => void;
  if (pageKey) {
    onClick = (pageDefinition: TemplatePageDefinition) => {
      dispatch({ type: "setPageToFocus", pageKey });
      dispatch({
        options: configuration?.options,
        pageDefinitionKey: pageDefinition.key,
        pageKey,
        type: "changePagePageDefinition",
        undoKey,
      });
    };
  } else {
    onClick = (pageDefinition: TemplatePageDefinition) => {
      dispatch({
        options: configuration?.options,
        pageDefinitionKey: pageDefinition.key,
        type: "changeAllPagesPageDefinition",
        undoKey,
      });
    };
  }

  let currentPairOrientation: "rotated" | "non-rotated" = "non-rotated";
  if (currentPageDefinitionKey && rotatedPairs) {
    const currentRotationPair = rotatedPairs[currentPageDefinitionKey];
    if (currentRotationPair) {
      currentPairOrientation = currentRotationPair.which;
    }
  }

  let currentPageDefinition: TemplatePageDefinition | undefined;
  if (currentPageDefinitionKey) {
    currentPageDefinition = findPageDefinition(currentPageDefinitionKey, template);
  }

  const getLayoutsList = (pageDefinitions: TemplatePageDefinition[]) => {
    const layoutsList = pageDefinitions.map((pageDefinition) => {
      if (
        currentPageDefinition &&
        (currentPageDefinition.type !== pageDefinition.type ||
          currentPageDefinition.filterType !== pageDefinition.filterType)
      ) {
        return null;
      }
      if (
        rotatedPairs &&
        currentPairOrientation &&
        rotatedPairs[pageDefinition.key] &&
        rotatedPairs[pageDefinition.key].which !== currentPairOrientation
      ) {
        return null;
      }
      return (
        <div
          className={classNames("PageLayouts-group-item", {
            "PageLayouts-group-item--selected": pageDefinition.key === selectedPageKey,
          })}
          key={pageDefinition.key}
          onClick={() => onClick(pageDefinition)}
        >
          <MockPage
            pageDefinitionKey={pageDefinition.key}
            restrictedSize={MAX_LAYOUT_SIZE}
            selected={pageDefinition.key === selectedPageKey}
          />
        </div>
      );
    });
    return layoutsList.reduce((finalList: JSX.Element[], layoutElement) => {
      if (layoutElement) {
        finalList.push(layoutElement);
      }
      return finalList;
    }, []);
  };

  return (
    <div className="PageLayouts" ref={pageLayoutsElement}>
      {layoutsGroupKeys.map((groupKey) => {
        const layoutsList = getLayoutsList(groupedPageDefinitions[groupKey].templates);
        if (layoutsList.length === 0) {
          return null;
        }

        return (
          <>
            <div className="PageLayouts-groupTitle">{groupedPageDefinitions[groupKey].title}</div>
            <div className="PageLayouts-group" key={groupedPageDefinitions[groupKey].title}>
              {layoutsList}
            </div>
          </>
        );
      })}
    </div>
  );
};

export default PageLayouts;
